import { Builder } from '@builder.io/react';
import React from 'react';

import { Button } from '../../../builder-io/storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import SocialProofCard from '../elements/SocialProofCard';

interface SocialProofProps {
  title: string;
  subtitle: string;
  description: string;
  backgroundColor?: string;
  cards: Array<{
    image: string;
    name: string;
    type: 'logo' | 'creator';
    alt?: string;
    mobileOnly?: boolean;
  }>;
  showCallToAction: boolean;
  callToAction?: {
    text: string;
    link: string;
  };
  showHighlightedCard: boolean;
  highlightedCard: {
    image: string;
    name: string;
    type: 'logo' | 'creator';
    alt: string;
  };
  builderState: Record<string, any>;
}

export default function SocialProof({
  title,
  subtitle,
  description,
  backgroundColor,
  cards = [],
  showCallToAction,
  callToAction,
  showHighlightedCard,
  highlightedCard: { image, name, type, alt },
  builderState = {},
}: SocialProofProps) {
  const isMobile = builderState?.state?.device === 'mobile';
  const styledGridCols = showHighlightedCard
    ? 'lg:grid-cols-3'
    : 'lg:grid-cols-2';
  return (
    <div
      className="transition-all duration-300 lg:max-h-50 lg:mt-20 lg:mb-24 lg:px-12"
      style={{ backgroundColor }}
    >
      <div
        className={`px-6 py-8 lg:p-0 lg:grid ${styledGridCols} lg:gap-8 max-w-90 mx-auto`}
      >
        <section className="mb-4 lg:pt-32">
          <Text
            fontSize="5xl"
            fontFamily="polysans"
            className="pb-3 italic"
            asChild
          >
            <h1>{title}</h1>
          </Text>
          <Text
            fontSize="2xl"
            fontFamily="polysans"
            className="pb-3 mt-3"
            asChild
          >
            <h2>{subtitle}</h2>
          </Text>
          <Text fontSize="xl" fontWeight="regular">
            <p>{description}</p>
          </Text>
          {showCallToAction && (
            <Button
              variant="secondary"
              className="mt-4"
              size="large"
              {...callToAction}
            >
              {callToAction?.text}
            </Button>
          )}
          <section className="grid grid-cols-2 grid-flow-dense gap-4 lg:gap-8 pt-8">
            {cards
              .slice(0, 4)
              .filter(({ mobileOnly }) => !mobileOnly || isMobile)
              .map(({ image, name, type, alt }, id) => (
                <SocialProofCard
                  key={id}
                  image={image}
                  name={name}
                  type={type}
                  alt={alt}
                />
              ))}
          </section>
        </section>
        <section className="lg:-mt-12">
          <div className="grid grid-cols-2 grid-flow-dense gap-4 lg:gap-8">
            {cards
              .slice(4)
              .filter(({ mobileOnly }) => !mobileOnly || isMobile)
              .map(({ image, name, type, alt }, id) => (
                <SocialProofCard
                  key={id}
                  image={image}
                  name={name}
                  type={type}
                  className={id % 2 === 0 ? '' : 'lg:-top-6'}
                  alt={alt}
                />
              ))}
          </div>
        </section>
        {showHighlightedCard && (
          <section className="mt-8 lg:mt-0 pb-4">
            <SocialProofCard
              image={image}
              name={name}
              type={type}
              highlighted
              alt={alt}
            />
          </section>
        )}
      </div>
    </div>
  );
}

export const config = {
  name: 'Social Proof',
  inputs: [
    {
      name: 'title',
      type: 'string',
      defaultValue: '150,000+',
      helperText: 'The main title.',
      required: true,
    },
    {
      name: 'subtitle',
      type: 'string',
      defaultValue:
        'brands and creators trust Storyblocks to bring their ideas to life',
      helperText: 'Optional subtitle.',
      required: false,
    },
    {
      name: 'description',
      type: 'string',
      defaultValue:
        'Learn why Canon, Grace Wells, Global Citizen, and others choose Storyblocks.',
      required: false,
      helperText: 'Optional description text to provide more context.',
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'var(--color-blue-100, #e3f2ff)',
      helperText: 'Background color for the container.',
      required: false,
    },
    {
      name: 'cards',
      type: 'list',
      subFields: [
        {
          name: 'image',
          type: 'file',
          defaultValue: '',
          required: true,
          allowedFileTypes: ['jpg', 'png'],
        },
        {
          name: 'alt',
          type: 'string',
          defaultValue: '',
          helperText: 'Alternative text for the image, for accessibility.',
          required: true,
        },
        {
          name: 'type',
          type: 'string',
          defaultValue: 'creator',
          helperText: 'Select the type of card.',
          enum: [
            { label: 'Logo', value: 'logo' },
            { label: 'Creator', value: 'creator' },
          ],
          onChange: `(options) => {
              const type = options.get('type');
              if (type === 'logo') {
                  options.set('name', '');
              }
          }`,
          required: true,
        },
        {
          name: 'name',
          type: 'string',
          defaultValue: 'Creator Name',
          showIf: `options.get('type') === 'creator'`,
          helperText: 'Name of the person or brand being represented.',
          required: true,
        },
        {
          name: 'mobileOnly',
          type: 'boolean',
          defaultValue: false,
          helperText: 'Whether to show this card only on mobile devices.',
        },
      ],
      helperText: 'List of social proof cards to display.',
      onChange: (options) => {
        if (options.get('cards').length > 14) {
          options.set('cards', options.get('cards').slice(0, 14));
          alert(
            'Maximum number of cards is 14. Modify an existing card instead.'
          );
        }
      },
    },
    {
      name: 'showCallToAction',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Whether to show a call to action button.',
    },
    {
      name: 'callToAction',
      type: 'object',
      defaultValue: {
        text: 'Read Case Studies',
        link: '/',
      },
      subFields: [
        {
          name: 'text',
          type: 'string',
          defaultValue: 'Read Case Studies',
          helperText: 'Text for the call to action button.',
        },
        {
          name: 'link',
          type: 'string',
          defaultValue: '#',
          helperText: 'URL for the call to action button.',
        },
      ],
      helperText: 'Configuration for the call to action button.',
    },
    {
      name: 'showHighlightedCard',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Whether to show a highlighted social proof card.',
    },
    {
      name: 'highlightedCard',
      type: 'object',
      defaultValue: {
        image: '',
        name: '',
        alt: '',
        type: 'creator',
      },
      subFields: [
        {
          name: 'image',
          type: 'file',
          defaultValue: '',
          helperText: 'Image URL for the highlighted card.',
          required: true,
          allowedFileTypes: ['jpg', 'png'],
        },
        {
          name: 'alt',
          type: 'string',
          defaultValue: '',
          helperText: 'Alternative text for the highlighted card image.',
          required: true,
        },
        {
          name: 'name',
          type: 'string',
          defaultValue: 'Creator Name',
          helperText: 'Name for the highlighted card.',
          required: true,
        },
        {
          name: 'type',
          type: 'string',
          defaultValue: 'creator',
          helperText: 'Type of the highlighted card.',
          enum: [
            { label: 'Logo', value: 'logo' },
            { label: 'Creator', value: 'creator' },
          ],
          required: true,
        },
      ],
      helperText: 'Configuration for the highlighted social proof card.',
    },
  ],
};

Builder.registerComponent(SocialProof, config);
